import { useMutation, useQuery } from '@apollo/client';
import {
    CREATE_APPLICATION,
    DELETE_APPLICATION,
    GET_ACCREDITATION_APPLICATIONS,
    GET_APPLICATION_CONFIG,
    PROGRESS_APPLICATION,
    UPDATE_APPLICATION,
} from 'graphql/member';
import { useCallback, useEffect, useState } from 'react';
import {
    AccreditationType,
    CreateAccreditationApplicationCommandInput,
    DeleteAccreditationApplicationCommandInput,
    LockOwner,
    ProgressAccreditationApplicationCommandInput,
    UpdateAccreditationApplicationCommandInput,
} from 'graphql/__generated__/graphql';
import { IAccreditationApplication } from 'interfaces/IAccreditationApplication';

export const useAccreditationApplications = () => {
    // Queries
    const {
        data: appsData,
        loading: appsLoading,
        error: appsError,
        refetch: refetchApplications,
    } = useQuery(GET_ACCREDITATION_APPLICATIONS);

    const {
        data: configData,
        loading: configLoading,
        error: configError,
    } = useQuery(GET_APPLICATION_CONFIG);

    // Mutations
    const [createApplicationMutation] = useMutation(CREATE_APPLICATION);
    const [progressApplicationMutation] = useMutation(PROGRESS_APPLICATION);
    const [updateApplicationMutation] = useMutation(UPDATE_APPLICATION);
    const [deleteApplicationMutation] = useMutation(DELETE_APPLICATION);

    // State
    const [applications, setApplications] = useState<IAccreditationApplication[]>([]);
    const [accreditationTypes, setAccreditationTypes] = useState<AccreditationType[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [updating, setUpdating] = useState<boolean>(false);
    const [error, setError] = useState<Error | null>(null);

    // Process application data when it loads
    useEffect(() => {
        if (!appsLoading && !appsError && appsData?.accreditationApplications) {
            setApplications(appsData.accreditationApplications);
        }

        if (appsError) {
            setError(appsError);
            console.error('Error loading applications:', appsError);
        }

        updateLoadingState();
    }, [appsLoading, appsError, appsData]);

    // Process config data when it loads
    useEffect(() => {
        if (!configLoading && !configError && configData?.activeAccreditationTypes) {
            setAccreditationTypes(configData.activeAccreditationTypes);
        }

        if (configError) {
            setError(configError);
            console.error('Error loading configuration:', configError);
        }

        updateLoadingState();
    }, [configLoading, configError, configData]);

    // Helper to update loading state based on all data loading states
    const updateLoadingState = useCallback(() => {
        if (!appsLoading && !configLoading) {
            setLoading(false);
        }
    }, [appsLoading, configLoading]);

    // Create a new application
    const createApplication = async ({
        accreditationType,
        isRenewal,
    }: IAccreditationApplication): Promise<IAccreditationApplication> => {
        try {
            setUpdating(true);
            const application: CreateAccreditationApplicationCommandInput = {
                accreditationTypeId: accreditationType.accreditationTypeId,
                isRenewal,
            };

            const { data } = await createApplicationMutation({
                variables: { application },
            });

            const createdApp = data.accreditationApplicationCreate as IAccreditationApplication;
            setApplications((prevState) => [...prevState, createdApp]);
            return createdApp;
        } catch (error) {
            console.error('Error creating application:', error);
            setError(error instanceof Error ? error : new Error(String(error)));
            throw error;
        } finally {
            setUpdating(false);
        }
    };

    // Submit an application for review
    const submitApplication = async (
        submittedApplication: IAccreditationApplication,
        nextLockOwner: LockOwner = LockOwner.Supervisor
    ): Promise<IAccreditationApplication> => {
        try {
            setUpdating(true);
            const application: ProgressAccreditationApplicationCommandInput = {
                accreditationApplicationId: submittedApplication.accreditationApplicationId,
                nextLockOwner,
            };

            const { data } = await progressApplicationMutation({
                variables: { application },
            });

            await fetchApplications();
            return data.accreditationApplicationProgress;
        } catch (error) {
            console.error('Error submitting application:', error);
            setError(error instanceof Error ? error : new Error(String(error)));
            throw error;
        } finally {
            setUpdating(false);
        }
    };

    // Update an existing application
    const updateApplication = async (
        updatedApplication: IAccreditationApplication
    ): Promise<IAccreditationApplication> => {
        try {
            setUpdating(true);
            const application: UpdateAccreditationApplicationCommandInput = {
                accreditationApplicationId: updatedApplication.accreditationApplicationId,
                jsonData: updatedApplication.jsonData,
            };

            const { data } = await updateApplicationMutation({
                variables: { application },
            });

            const updatedApp = data.accreditationApplicationUpdate as IAccreditationApplication;
            setApplications((prevState) =>
                prevState.map((app) =>
                    app.accreditationApplicationId === updatedApp.accreditationApplicationId
                        ? updatedApp
                        : app
                )
            );

            return updatedApp;
        } catch (error) {
            console.error('Error updating application:', error);
            setError(error instanceof Error ? error : new Error(String(error)));
            throw error;
        } finally {
            setUpdating(false);
        }
    };

    // Delete an application
    const deleteApplication = async (
        deletedApplication: IAccreditationApplication
    ): Promise<void> => {
        setUpdating(true);
        try {
            const application: DeleteAccreditationApplicationCommandInput = {
                accreditationApplicationId: deletedApplication.accreditationApplicationId,
            };

            await deleteApplicationMutation({
                variables: { application },
            });

            // Refetch applications instead of manually updating state
            await fetchApplications();
        } catch (error) {
            console.error('Error deleting application:', error);
            setError(error instanceof Error ? error : new Error(String(error)));
            throw error;
        } finally {
            setUpdating(false);
        }
    };

    // Save application (create or update based on id presence)
    const upsertApplication = async (
        application: IAccreditationApplication
    ): Promise<IAccreditationApplication> => {
        return application.accreditationApplicationId
            ? updateApplication(application)
            : createApplication(application);
    };

    // Fetch updated applications data
    const fetchApplications = async (): Promise<IAccreditationApplication[]> => {
        setLoading(true);
        try {
            const { data } = await refetchApplications({ cachePolicy: 'no-cache' });
            const fetchedApps = data.accreditationApplications;
            setApplications(fetchedApps);
            return fetchedApps;
        } catch (error) {
            console.error('Error fetching applications:', error);
            setError(error instanceof Error ? error : new Error(String(error)));
            throw error;
        } finally {
            setLoading(false);
        }
    };

    return {
        models: {
            accreditationTypes,
            applications,
            error,
            loading,
            updating,
        },
        operations: {
            deleteApplication,
            fetchApplications,
            upsertApplication,
            submitApplication,
        },
    };
};
